import React from 'react';
import {
    TextField, MessageBar, MessageBarType, Stack, IconButton, Separator, Text,
} from '@fluentui/react';
import NumberField from '../Inputs/NumberField';
import PromoPricingFields from '../Storefront/PromoPricingFields';
import { IProductPromo, describePromoPricing } from '../../models/ProductPromo';

interface ProductPromoTabProps {
    promos?: IProductPromo[];
    /** Absent while the modal is read-only; the tab renders values without inputs. */
    onChange?: (promos: IProductPromo[]) => void;
    editMode: boolean;
}

/** UTC ISO -> the local wall-clock string a datetime-local input expects. */
const toLocalInput = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toUtc = (local: string) => (local ? new Date(local).toISOString() : undefined);

const describeWindow = (promo: IProductPromo) => {
    const now = new Date();
    if (promo.startsAt && new Date(promo.startsAt) > now) return { text: 'Scheduled', color: MessageBarType.info };
    if (promo.endsAt && new Date(promo.endsAt) <= now) return { text: 'Ended', color: MessageBarType.warning };
    if (promo.allocationQuantity != null
        && (promo.allocationSold ?? 0) >= promo.allocationQuantity) return { text: 'Sold out', color: MessageBarType.warning };
    return { text: 'Running', color: MessageBarType.success };
};

/**
 * Per-product view and editing of MSP promos.
 *
 * Vendor promos are deliberately absent: those are a promotional COST carried on the vendor record and
 * supplied by the distributor's own data, so they are neither authored nor editable here. What shows up
 * in this tab is only what the MSP has chosen to fund out of their own margin.
 */
const ProductPromoTab: React.FC<ProductPromoTabProps> = ({ promos, onChange, editMode }) => {
    const list = promos ?? [];

    const update = (index: number, patch: Partial<IProductPromo>) => {
        if (!onChange) return;
        onChange(list.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    };
    const remove = (index: number) => onChange?.(list.filter((_, i) => i !== index));

    if (list.length === 0) {
        return (
            <MessageBar messageBarType={MessageBarType.info}>
                No promos on this product. Create one by selecting products on the storefront's Products tab.
            </MessageBar>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            {list.map((promo, index) => {
                const status = describeWindow(promo);
                const remaining = promo.allocationQuantity != null
                    ? Math.max(0, promo.allocationQuantity - (promo.allocationSold ?? 0))
                    : undefined;
                return (
                    <Stack key={promo.id + index} tokens={{ childrenGap: 8 }}>
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                            <Text variant="mediumPlus">{promo.name || 'Untitled promo'}</Text>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                <Text variant="small">{status.text}</Text>
                                {editMode && (
                                    <IconButton
                                        iconProps={{ iconName: 'Delete' }}
                                        title="Remove this promo from the product"
                                        ariaLabel="Remove promo"
                                        onClick={() => remove(index)}
                                    />
                                )}
                            </Stack>
                        </Stack>

                        <TextField
                            label="Promo name"
                            readOnly={!editMode}
                            borderless={!editMode}
                            value={promo.name ?? ''}
                            onChange={(_, v) => update(index, { name: v })}
                        />
                        {/* An entry holds OVERRIDES: unset means "whatever the campaign says", which is
                            what most entries are and what the read-only view has to say rather than
                            printing the campaign's default as if this product had chosen it. */}
                        {editMode ? (
                            <PromoPricingFields
                                markupType={promo.markupType}
                                markup={promo.markup}
                                allowInherit
                                onChange={(markupType, markup) => update(index, { markupType, markup })}
                            />
                        ) : (
                            <TextField
                                label="Promotional pricing"
                                readOnly
                                borderless
                                value={promo.markup === undefined || promo.markup === null
                                    ? 'Inherited from the promotion'
                                    : describePromoPricing(promo.markupType, promo.markup)}
                            />
                        )}

                        <Stack horizontal tokens={{ childrenGap: 12 }}>
                            <TextField
                                label="Starts"
                                type="datetime-local"
                                readOnly={!editMode}
                                value={toLocalInput(promo.startsAt)}
                                onChange={(_, v) => update(index, { startsAt: toUtc(v ?? '') })}
                            />
                            <TextField
                                label="Ends"
                                type="datetime-local"
                                readOnly={!editMode}
                                value={toLocalInput(promo.endsAt)}
                                onChange={(_, v) => update(index, { endsAt: toUtc(v ?? '') })}
                            />
                        </Stack>

                        <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
                            <NumberField
                                label="Units at the promo price"
                                readonly={!editMode}
                                toolTip="Leave blank for no limit. Once these are sold the promo stops applying and the normal price returns."
                                value={promo.allocationQuantity}
                                onChange={(v) => update(index, { allocationQuantity: v })}
                            />
                            {promo.allocationQuantity != null && (
                                <Text variant="small" styles={{ root: { paddingBottom: 8 } }}>
                                    {promo.allocationSold ?? 0} sold, {remaining} remaining
                                </Text>
                            )}
                        </Stack>

                        <Text variant="small">
                            {promo.storefronts && promo.storefronts.length > 0
                                ? `Applies to ${promo.storefronts.length} storefront${promo.storefronts.length === 1 ? '' : 's'}.`
                                : 'Applies to every storefront.'}
                        </Text>

                        {index < list.length - 1 && <Separator />}
                    </Stack>
                );
            })}

            {editMode && (
                <MessageBar messageBarType={MessageBarType.info}>
                    The shopper always gets the lower of the promo price and the normal price, so a promo
                    that would raise the price simply never applies. Promos do not stack with a vendor's
                    own promotional cost: whichever is cheaper on its own wins.
                </MessageBar>
            )}
        </Stack>
    );
};

export default ProductPromoTab;
